import React, { useState, useEffect, useMemo } from 'react';
import { Alert, Form, Button, Collapse, Card, FilterGrid, FilterField } from '@/components/ui';
import reportService from '../../../services/reportService';
import ReportParametersCard from '../components/ReportParametersCard';
import ReportPreviewTable from '../components/ReportPreviewTable';
import ExportOptionsModal from '../components/ExportOptionsModal';
import Toast from '../../../components/Toast';
import { ServiceArea } from '../../../services/areasService';
import {
  employeeOptionName,
  useEmployeeOptions,
  getForemanAreaOptions
} from '../utils/reportFilterOptions';

type WeeklyOvertimePreviewRow = {
  id: string;
  Date: string;
  'Service Area': string;
  'Foreman Area': string;
  'Employee Name': string;
  'Employee ID': string;
  'Actual Start Time': string;
  'Actual End Time': string;
  'Actual Hours Worked (excluding Lunch)': string;
  'Overtime Worked?': string;
  Comment: string;
  'Work through lunch?': string;
  'Approver Name': string;
  'CTO / Paid-out': string;
  Unscheduled: string;
  'Number of Overtime Meals': string;
};

const PREVIEW_COLUMNS = [
  { label: 'Date', key: 'Date', sortable: false, width: '100px' },
  { label: 'SA', key: 'Service Area', sortable: false, width: '70px' },
  { label: 'Foreman', key: 'Foreman Area', sortable: false, width: '80px' },
  { label: 'Employee', key: 'Employee Name', sortable: false, width: '120px' },
  { label: 'ID', key: 'Employee ID', sortable: false, width: '90px' },
  { label: 'Start', key: 'Actual Start Time', sortable: false, width: '60px' },
  { label: 'End', key: 'Actual End Time', sortable: false, width: '60px' },
  { label: 'Hours', key: 'Actual Hours Worked (excluding Lunch)', sortable: false, width: '70px' },
  { label: 'OT?', key: 'Overtime Worked?', sortable: false, width: '50px' },
  { label: 'Comment', key: 'Comment', sortable: false, width: '120px' },
  { label: 'Lunch', key: 'Work through lunch?', sortable: false, width: '70px' },
  { label: 'Approver', key: 'Approver Name', sortable: false, width: '100px' },
  { label: 'CTO', key: 'CTO / Paid-out', sortable: false, width: '90px' },
  { label: 'Unsch.', key: 'Unscheduled', sortable: false, width: '80px' },
  { label: 'OT meals', key: 'Number of Overtime Meals', sortable: false, width: '70px' }
];

interface WeeklyOvertimeReportViewProps {
  serviceAreas: ServiceArea[];
  canCreateWeeklyPayroll: boolean;
  loadingServiceAreas: boolean;
  defaultServiceAreaId?: string;
}

const WeeklyOvertimeReportView: React.FC<WeeklyOvertimeReportViewProps> = ({
  serviceAreas,
  canCreateWeeklyPayroll,
  loadingServiceAreas,
  defaultServiceAreaId
}) => {
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    serviceArea: defaultServiceAreaId || '',
    employeeName: '',
    foremanArea: '',
    exportStatus: 'unexported' as 'unexported' | 'exported' | 'all'
  });
  const [previewData, setPreviewData] = useState<WeeklyOvertimePreviewRow[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    onPageChange: (page: number, pageSize?: number) => {
      void page;
      void pageSize;
    }
  });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingPreliminary, setLoadingPreliminary] = useState(false);
  const [loadingFinal, setLoadingFinal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [dateFilterType, setDateFilterType] = useState<'approval' | 'formDate'>('approval');

  const clearPreview = () => {
    setPreviewData([]);
    setPagination((prev) => ({ ...prev, total: 0, totalPages: 0 }));
  };

  const handleDateFilterTypeChange = (value: 'approval' | 'formDate') => {
    setDateFilterType(value);
    clearPreview();
  };

  const employeeOptions = useEmployeeOptions(filters.serviceArea);
  const foremanAreaOptions = useMemo(
    () => getForemanAreaOptions(serviceAreas, filters.serviceArea),
    [serviceAreas, filters.serviceArea]
  );

  useEffect(() => {
    if (defaultServiceAreaId && !filters.serviceArea) {
      setFilters((f) => ({ ...f, serviceArea: defaultServiceAreaId }));
    }
  }, [defaultServiceAreaId]);

  // Clear employee/foreman selections that fall outside the selected service area.
  useEffect(() => {
    setFilters((f) => {
      const foremanValid =
        !f.foremanArea || foremanAreaOptions.some((fa) => String(fa.id) === f.foremanArea);
      const employeeValid =
        !f.employeeName || employeeOptions.some((emp) => employeeOptionName(emp) === f.employeeName);
      if (foremanValid && employeeValid) return f;
      return {
        ...f,
        foremanArea: foremanValid ? f.foremanArea : '',
        employeeName: employeeValid ? f.employeeName : ''
      };
    });
  }, [employeeOptions, foremanAreaOptions]);

  const handlePageChange = (page: number, pageSize?: number) => {
    void handleGeneratePreview(page, pageSize);
  };

  const mapRow = (row: Record<string, string>, pageNum: number, i: number): WeeklyOvertimePreviewRow => ({
    id: `${pageNum}-${i}`,
    Date: String(row.Date ?? ''),
    'Service Area': String(row['Service Area'] ?? ''),
    'Foreman Area': String(row['Foreman Area'] ?? ''),
    'Employee Name': String(row['Employee Name'] ?? ''),
    'Employee ID': String(row['Employee ID'] ?? ''),
    'Actual Start Time': String(row['Actual Start Time'] ?? ''),
    'Actual End Time': String(row['Actual End Time'] ?? ''),
    'Actual Hours Worked (excluding Lunch)': String(row['Actual Hours Worked (excluding Lunch)'] ?? ''),
    'Overtime Worked?': String(row['Overtime Worked?'] ?? ''),
    Comment: String(row.Comment ?? ''),
    'Work through lunch?': String(row['Work through lunch?'] ?? ''),
    'Approver Name': String(row['Approver Name'] ?? ''),
    'CTO / Paid-out': String(row['CTO / Paid-out'] ?? ''),
    Unscheduled: String(row.Unscheduled ?? ''),
    'Number of Overtime Meals': String(row['Number of Overtime Meals'] ?? '')
  });

  const handleGeneratePreview = async (page: number = 1, pageSize?: number) => {
    setLoadingPreview(true);
    setErrorMessage(null);
    try {
      const res = await reportService.getWeeklyOvertimePreview({
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        dateFilterType,
        serviceArea: filters.serviceArea || undefined,
        employeeName: filters.employeeName || undefined,
        foremanArea: filters.foremanArea || undefined,
        exportStatus: filters.exportStatus,
        page,
        limit: pageSize || pagination.limit
      });
      const limit = res.pagination.limit;
      const pageNum = res.pagination.page;
      const rows: WeeklyOvertimePreviewRow[] = (res.data as Record<string, string>[]).map((row, i) =>
        mapRow(row, pageNum, i)
      );
      setPreviewData(rows);
      setPagination({
        page: res.pagination.page,
        limit,
        total: res.pagination.total,
        totalPages: res.pagination.totalPages,
        onPageChange: handlePageChange
      });
    } catch {
      setErrorMessage('Failed to load weekly overtime preview.');
      setPreviewData([]);
    } finally {
      setLoadingPreview(false);
    }
  };

  const exportReport = async (dryRun: boolean) => {
    if (!canCreateWeeklyPayroll) return;
    setErrorMessage(null);
    try {
      await reportService.exportWeeklyOvertimeReport({
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        dateFilterType,
        serviceArea: filters.serviceArea || undefined,
        employeeName: filters.employeeName || undefined,
        foremanArea: filters.foremanArea || undefined,
        exportStatus: filters.exportStatus,
        dryRun
      });
      setToastMessage(
        dryRun
          ? 'Preliminary report downloaded (forms not marked exported).'
          : 'Weekly overtime exported.'
      );
      setShowToast(true);
      setShowExportModal(false);
      await handleGeneratePreview(pagination.page, pagination.limit);
    } catch {
      setErrorMessage('Failed to export weekly overtime report.');
    }
  };

  const handlePreliminaryExport = async () => {
    setLoadingPreliminary(true);
    try {
      await exportReport(true);
    } finally {
      setLoadingPreliminary(false);
    }
  };

  const handleFinalizedExport = async () => {
    setLoadingFinal(true);
    try {
      await exportReport(false);
    } finally {
      setLoadingFinal(false);
    }
  };
  const tableColumns = PREVIEW_COLUMNS.map((c) => ({
    ...c,
    render: (row: WeeklyOvertimePreviewRow) => String(row[c.key as keyof WeeklyOvertimePreviewRow] ?? '')
  }));

  const handlePageSizeChange = (newPageSize: number) => {
    void handleGeneratePreview(1, newPageSize);
  };

  return (
    <div className="tw-flex tw-flex-col" style={{ height: '100%', minHeight: 0 }}>
      <h3 className="tw-mb-3">Overtime Report</h3>
      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

      <ReportParametersCard
        onGenerate={() => void handleGeneratePreview(1)}
        onExport={() => {}}
        loadingGenerate={loadingPreview}
        loadingExport={false}
        disableExport={true}
        generateButtonText="Run Report"
        hideExportButton={true}
        defaultCollapsed={false}
        onCollapseChange={setIsFiltersCollapsed}
      >
        <FilterGrid min="lg">
          <Form.Group className="tw-mb-1">
            <Form.Label className="tw-text-sm tw-mb-1">Service Area</Form.Label>
            <Form.Select
              size="sm"
              value={filters.serviceArea}
              onChange={(e) => setFilters((f) => ({ ...f, serviceArea: e.target.value }))}
              disabled={loadingServiceAreas}
            >
              <option value="">All</option>
              {serviceAreas.map((a) => (
                <option key={a.id} value={String(a.id)}>
                  {a.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <FilterField span={2}>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Date range</Form.Label>
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <span className="tw-text-content-muted tw-text-sm">Filter by:</span>
                  <Form.Check
                    type="radio"
                    id="overtime-dateFilter-approval"
                    name="overtimeDateFilterType"
                    label="Approval date"
                    checked={dateFilterType === 'approval'}
                    onChange={() => handleDateFilterTypeChange('approval')}
                    className="tw-text-sm"
                  />
                  <Form.Check
                    type="radio"
                    id="overtime-dateFilter-formDate"
                    name="overtimeDateFilterType"
                    label="Form date"
                    checked={dateFilterType === 'formDate'}
                    onChange={() => handleDateFilterTypeChange('formDate')}
                    className="tw-text-sm"
                  />
                </div>
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                  <Form.Control
                    size="sm"
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
                    style={{ width: '140px' }}
                  />
                  <span className="tw-text-content-muted tw-text-sm">to</span>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
                    style={{ width: '140px' }}
                  />
                </div>
              </div>
              <Form.Text className="tw-text-content-muted">
                {dateFilterType === 'approval'
                  ? 'Includes forms approved in this range; row dates reflect work performed.'
                  : 'Includes forms whose form date falls in this range; forms without a form date are excluded.'}
              </Form.Text>
            </Form.Group>
          </FilterField>
        </FilterGrid>

        <div className="tw-mt-3">
          <Button
            variant="link"
            size="sm"
            onClick={() => setShowAdditionalFilters(!showAdditionalFilters)}
            className="tw-p-0 tw-no-underline"
            style={{ fontSize: '0.875rem' }}
          >
            <i className={`bi bi-chevron-${showAdditionalFilters ? 'up' : 'down'} tw-mr-1`}></i>
            Additional Filters
          </Button>
          <Collapse in={showAdditionalFilters}>
            <div>
              <FilterGrid className="tw-mt-2">
                <Form.Group className="tw-mb-1">
                  <Form.Label className="tw-text-sm tw-mb-1">Overtime export status</Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.exportStatus}
                    onChange={(e) =>
                      setFilters((f) => ({
                        ...f,
                        exportStatus: e.target.value as 'unexported' | 'exported' | 'all'
                      }))
                    }
                  >
                    <option value="unexported">Unexported</option>
                    <option value="exported">Exported</option>
                    <option value="all">All</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="tw-mb-1">
                  <Form.Label className="tw-text-sm tw-mb-1">Employee name</Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.employeeName}
                    onChange={(e) => setFilters((f) => ({ ...f, employeeName: e.target.value }))}
                  >
                    <option value="">All</option>
                    {employeeOptions.map((emp) => (
                      <option key={emp.id} value={employeeOptionName(emp)}>
                        {employeeOptionName(emp)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="tw-mb-1">
                  <Form.Label className="tw-text-sm tw-mb-1">Foreman area</Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.foremanArea}
                    onChange={(e) => setFilters((f) => ({ ...f, foremanArea: e.target.value }))}
                  >
                    <option value="">All</option>
                    {foremanAreaOptions.map((fa) => (
                      <option key={fa.id} value={String(fa.id)}>
                        {fa.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </FilterGrid>
            </div>
          </Collapse>
        </div>
      </ReportParametersCard>

      <div className="tw-grow tw-mt-2" style={{ minHeight: 0, minWidth: 0, overflow: 'auto' }}>
        <Card style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
          <Card.Header className="tw-flex tw-justify-between tw-items-center">
            <h6 className="tw-mb-0">Report Data ({pagination.total} records)</h6>
            {pagination.total > 0 && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowExportModal(true)}
                disabled={!canCreateWeeklyPayroll}
              >
                <i className="bi bi-download tw-mr-1"></i>
                Export Report
              </Button>
            )}
          </Card.Header>
          <Card.Body className="tw-p-0" style={{ flex: 1, minHeight: 0 }}>
            <ReportPreviewTable<WeeklyOvertimePreviewRow>
              columns={tableColumns}
              data={previewData}
              loading={loadingPreview}
              pagination={{
                page: pagination.page,
                limit: pagination.limit,
                total: pagination.total,
                totalPages: pagination.totalPages,
                onPageChange: handlePageChange,
                onPageSizeChange: handlePageSizeChange
              }}
              isFiltersCollapsed={isFiltersCollapsed}
              emptyMessage="No data available. Click 'Run Report' to load data."
            />
          </Card.Body>
        </Card>
      </div>

      <ExportOptionsModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onPreliminaryExport={() => void handlePreliminaryExport()}
        onFinalExport={() => void handleFinalizedExport()}
        loadingPreliminary={loadingPreliminary}
        loadingFinal={loadingFinal}
        isPayroll={true}
        size="small"
      />
      <Toast type="success" message={toastMessage} show={showToast} onClose={() => setShowToast(false)} />
    </div>
  );
};

export default WeeklyOvertimeReportView;
